import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useLang, LangToggle } from "../lib/i18n";
import { useAuth, ADMIN_ROLES } from "../lib/auth";
import { api, errMsg } from "../lib/api";
import { Logo } from "../components/Logo";
import { Notice } from "../components/ui-kit";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-slate-500 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-all";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.98 11.98 0 0 0 12 0 11.99 11.99 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

export function GoogleButton() {
  const { t } = useLang();
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} data-testid="google-login-btn" className="flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-white/5 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10">
      <GoogleIcon /> {t("Continue with Google", "Google से जारी रखें")}
    </button>
  );
}

export function AuthCallback() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = window.location.hash.split("session_id=")[1]?.split("&")[0];
    if (!sid) { nav("/login", { replace: true }); return; }
    api.post("/auth/google/session", { session_id: sid })
      .then((r) => {
        setUser(r.data);
        window.history.replaceState(null, "", window.location.pathname);
        nav(ADMIN_ROLES.includes(r.data.role) ? "/admin" : "/app", { replace: true });
      })
      .catch((e) => { toast.error(errMsg(e)); nav("/login", { replace: true }); });
  }, []); // eslint-disable-line
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#05070A]" data-testid="auth-callback">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
    </div>
  );
}

function Shell({ title, sub, children }) {
  const { t } = useLang();
  return (
    <div className="grid min-h-screen bg-[#05070A] lg:grid-cols-2">
      <div className="relative hidden flex-col justify-between bg-[#0A0D14] p-12 text-white lg:flex overflow-hidden border-r border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.1),transparent_50%)]" />
        <div className="absolute bottom-0 left-0 h-[500px] w-[500px] bg-[radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.1),transparent_50%)]" />
        <div className="relative z-10"><Logo light /></div>
        <div className="relative z-10">
          <p className="text-5xl font-black leading-tight tracking-tight">{t("Complete Surveys", "सर्वे करें")}<br /><span className="text-gradient-gold">{t("Earn Rewards", "रिवॉर्ड कमाएँ")}</span></p>
          <p className="mt-6 text-lg font-medium text-slate-400">{t("Your opinion builds a better society and better opportunities.", "आपकी राय से बेहतर समाज और बेहतर अवसर।")}</p>
        </div>
        <p className="relative z-10 text-xs font-bold uppercase tracking-widest text-slate-600">{t("Registration is always free. Rewards are not guaranteed.", "रजिस्ट्रेशन हमेशा मुफ्त है। रिवॉर्ड की गारंटी नहीं है।")}</p>
      </div>
      <div className="flex flex-col bg-[#05070A]">
        <div className="flex items-center justify-between p-6"><div className="lg:hidden"><Logo small light /></div><div className="ml-auto"><LangToggle /></div></div>
        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-6 pb-16">
          <h1 className="text-4xl font-black tracking-tight text-white" data-testid="auth-title">{title}</h1>
          {sub && <p className="mt-3 text-sm font-medium text-slate-400">{sub}</p>}
          <div className="mt-10">{children}</div>
        </div>
      </div>
    </div>
  );
}

function OtpStep({ mobile, devOtp, onDone }) {
  const { t } = useLang();
  const [code, setCode] = useState("");
  const [dev, setDev] = useState(devOtp);
  const [busy, setBusy] = useState(false);
  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/auth/verify-otp", { mobile, code }); onDone(data); }
    catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  const resend = async () => {
    try { const { data } = await api.post("/auth/resend-otp", { mobile }); setDev(data.dev_otp); toast.success(t("OTP resent", "OTP दोबारा भेजा")); }
    catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <form onSubmit={verify} className="space-y-5" data-testid="otp-form">
      <p className="text-sm font-medium text-slate-400">{t("Enter the 6-digit OTP sent to", "भेजा गया 6 अंकों का OTP दर्ज करें")} <b className="text-white">+91 {mobile}</b></p>
      {dev && <Notice tone="purple" testid="mock-otp-notice">MOCK OTP (SMS not connected): <b data-testid="mock-otp-value">{dev}</b></Notice>}
      <input className={`${inp} text-center text-3xl tracking-[0.5em] font-black`} maxLength={6} inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="otp-input" required />
      <button disabled={busy || code.length !== 6} className="w-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B8860B] py-4 text-sm font-black uppercase tracking-wider text-[#05070A] shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all hover:shadow-[0_0_25px_rgba(212,175,55,0.5)] disabled:opacity-50" data-testid="otp-verify-btn">{t("Verify & continue", "सत्यापित करें")}</button>
      <button type="button" onClick={resend} className="w-full text-sm font-bold text-[#D4AF37] hover:text-white transition-colors" data-testid="otp-resend-btn">{t("Resend OTP", "OTP दोबारा भेजें")}</button>
    </form>
  );
}

export function Register() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", mobile: "", email: "", password: "", referral_code: sp.get("ref") || "", consent: false, website: "" });
  const [otp, setOtp] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/auth/register", { ...f, referral_code: f.referral_code || null }); setOtp(data); }
    catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  const done = (u) => { setUser(u); toast.success(t("Welcome to SurveyKaro!", "SurveyKaro में स्वागत है!")); nav(sp.get("next") || "/app"); };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Shell title={otp ? t("Verify your mobile", "मोबाइल सत्यापित करें") : t("Create your free Partner account", "अपना मुफ्त पार्टनर अकाउंट बनाएँ")} sub={!otp && t("No payment required to register.", "रजिस्टर करने के लिए कोई भुगतान नहीं।")}>
      {otp ? <OtpStep mobile={otp.mobile} devOtp={otp.dev_otp} onDone={done} /> : (
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <GoogleButton />
          <div className="flex items-center gap-3 py-1"><span className="h-px flex-1 bg-white/10" /><span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{t("or register with mobile", "या मोबाइल से रजिस्टर करें")}</span><span className="h-px flex-1 bg-white/10" /></div>
          <input className={inp} placeholder={t("Full name", "पूरा नाम")} value={f.name} onChange={set("name")} data-testid="reg-name" required />
          <div className="flex"><span className="flex items-center rounded-l-xl border border-r-0 border-white/10 bg-white/5 px-4 text-sm font-bold text-slate-400">+91</span><input className={`${inp} rounded-l-none`} placeholder={t("Mobile number", "मोबाइल नंबर")} inputMode="numeric" maxLength={10} value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value.replace(/\D/g, "") })} data-testid="reg-mobile" required /></div>
          <input className={inp} type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="reg-email" required />
          <input className={inp} type="password" placeholder={t("Password (min 8 characters)", "पासवर्ड (कम से कम 8 अक्षर)")} minLength={8} value={f.password} onChange={set("password")} data-testid="reg-password" required />
          <input className={inp} placeholder={t("Referral code (optional)", "रेफरल कोड (वैकल्पिक)")} value={f.referral_code} onChange={set("referral_code")} data-testid="reg-referral" />
          <input className="hidden" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} />
          <label className="flex items-start gap-3 text-xs font-medium text-slate-400 cursor-pointer">
            <input type="checkbox" className="mt-1 accent-[#D4AF37]" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} data-testid="reg-consent" />
            <span>{t("I agree to the", "मैं सहमत हूँ")} <Link to="/policy/terms" className="text-[#D4AF37] hover:text-white transition-colors">{t("Terms", "नियम")}</Link> & <Link to="/policy/privacy" className="text-[#D4AF37] hover:text-white transition-colors">{t("Privacy Policy", "गोपनीयता नीति")}</Link></span>
          </label>
          <button disabled={busy || !f.consent} className="w-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B8860B] py-4 text-sm font-black uppercase tracking-wider text-[#05070A] shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all hover:shadow-[0_0_25px_rgba(212,175,55,0.5)] disabled:opacity-50" data-testid="reg-submit">{t("Register & send OTP", "रजिस्टर करें और OTP भेजें")}</button>
          <p className="text-center text-sm font-medium text-slate-400 pt-2">{t("Already a Partner?", "पहले से पार्टनर हैं?")} <Link to="/login" className="font-bold text-[#D4AF37] hover:text-white transition-colors" data-testid="go-login">{t("Login", "लॉगिन")}</Link></p>
        </form>
      )}
    </Shell>
  );
}

export function Login() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ identifier: "", password: "" });
  const [otp, setOtp] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = (u) => { setUser(u); nav(ADMIN_ROLES.includes(u.role) ? "/admin" : sp.get("next") || "/app"); };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/auth/login", f); go(data); }
    catch (er) {
      const d = er?.response?.data?.detail;
      if (d?.code === "verify_otp") setOtp({ mobile: d.mobile, dev_otp: d.dev_otp });
      else toast.error(errMsg(er));
    }
    setBusy(false);
  };
  return (
    <Shell title={otp ? t("Verify your mobile", "मोबाइल सत्यापित करें") : t("Welcome back", "फिर से स्वागत है")} sub={!otp && t("Login with email or mobile", "ईमेल या मोबाइल से लॉगिन करें")}>
      {otp ? <OtpStep mobile={otp.mobile} devOtp={otp.dev_otp} onDone={go} /> : (
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <GoogleButton />
          <div className="flex items-center gap-3 py-1"><span className="h-px flex-1 bg-white/10" /><span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{t("or login with email/mobile", "या ईमेल/मोबाइल से लॉगिन करें")}</span><span className="h-px flex-1 bg-white/10" /></div>
          <input className={inp} placeholder={t("Email or mobile", "ईमेल या मोबाइल")} value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} data-testid="login-identifier" required />
          <input className={inp} type="password" placeholder={t("Password", "पासवर्ड")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password" required />
          <div className="text-right pt-1"><Link to="/forgot-password" className="text-xs font-bold text-[#D4AF37] hover:text-white transition-colors" data-testid="forgot-link">{t("Forgot password?", "पासवर्ड भूल गए?")}</Link></div>
          <button disabled={busy} className="w-full rounded-full bg-gradient-to-r from-blue-600 to-blue-500 py-4 text-sm font-black uppercase tracking-wider text-white shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all hover:shadow-[0_0_25px_rgba(59,130,246,0.5)] disabled:opacity-50" data-testid="login-submit">{t("Login", "लॉगिन")}</button>
          <p className="text-center text-sm font-medium text-slate-400 pt-2">{t("New here?", "नए हैं?")} <Link to="/register" className="font-bold text-[#D4AF37] hover:text-white transition-colors" data-testid="go-register">{t("Create free account", "मुफ्त अकाउंट बनाएँ")}</Link></p>
        </form>
      )}
    </Shell>
  );
}

export function Forgot() {
  const { t } = useLang();
  const [id, setId] = useState("");
  const [res, setRes] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/auth/forgot-password", { identifier: id }); setRes(data); }
    catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <Shell title={t("Reset your password", "पासवर्ड रीसेट करें")}>
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <input className={inp} placeholder={t("Email or mobile", "ईमेल या मोबाइल")} value={id} onChange={(e) => setId(e.target.value)} data-testid="forgot-identifier" required />
        <button className="w-full rounded-full bg-gradient-to-r from-blue-600 to-blue-500 py-4 text-sm font-black uppercase tracking-wider text-white shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all hover:shadow-[0_0_25px_rgba(59,130,246,0.5)]" data-testid="forgot-submit">{t("Send reset link", "रीसेट लिंक भेजें")}</button>
      </form>
      {res && <div className="mt-6 space-y-3"><Notice tone="blue">{res.message}</Notice><Notice tone="purple" testid="reset-support-note">{t("Check your registered email inbox (and spam folder). The link is valid for 1 hour. Still no email? Contact support@surveykaro.in.", "अपने पंजीकृत ईमेल का इनबॉक्स (और स्पैम फ़ोल्डर) देखें। लिंक 1 घंटे तक मान्य है। ईमेल न मिले तो support@surveykaro.in पर संपर्क करें।")}</Notice></div>}
    </Shell>
  );
}

export function Reset() {
  const { t } = useLang();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); toast.success(t("Password updated. Please login.", "पासवर्ड बदला गया। लॉगिन करें।")); nav("/login"); }
    catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <Shell title={t("Choose a new password", "नया पासवर्ड चुनें")}>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <input className={inp} type="password" minLength={8} placeholder={t("New password", "नया पासवर्ड")} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" required />
        <button className="w-full rounded-full bg-gradient-to-r from-blue-600 to-blue-500 py-4 text-sm font-black uppercase tracking-wider text-white shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all hover:shadow-[0_0_25px_rgba(59,130,246,0.5)]" data-testid="reset-submit">{t("Update password", "पासवर्ड अपडेट करें")}</button>
      </form>
    </Shell>
  );
}
